import { createEffect, createEvent, createStore, sample } from 'effector';
import { axiosClient } from '@nx-frontend/shared/api';
import { aiServiceCommonApiClient } from '@nx-frontend/shared/next-api/proxy/clients';
import { AgentThreadInput, AIAgent, AIWebPageDetails } from '@nx-frontend/shared/types';
import { $aiThreads, $writingMessage } from './store';
import {
  AgentInput,
  ChatBotAttachedFile,
  ChatbotMessage,
  ChatbotMessageRole,
  ChatbotThread,
  MessageChunk,
  MessageChunkType,
} from './types';
import { createAgentMessage } from './util';

type AIHelperAgentStoreState = 'not_loaded' | 'loading' | 'loaded' | 'error';

interface AIHelperAgentStore {
  agent?: AIAgent;
  state: AIHelperAgentStoreState;
}

interface SendAgentMessageParams {
  threadId: string;
  sessionId?: string | null;
  apiHost?: string;
  workspaceId: string;
  orgId: string;
  text: string | null;
  onMessageChunkResolved: (payload: MessageChunk) => void;
  abortController: AbortController;
  currentLocationTitle?: string | null;
  /**
   * Array of tempStoredFile names
   */
  files?: ChatBotAttachedFile[];
  webPageDetails?: AIWebPageDetails;
  rewriteId?: string;
}

export const loadAIHelperAgentEvent = createEvent<{
  globalId: string;
  orgId: string;
}>();

const aiHelperAgentInitialState = {
  state: 'not_loaded' as AIHelperAgentStoreState,
};

export const createAgentMessageEvent = createEvent<SendAgentMessageParams>();

export const abortAgentMessageEvent = createEvent<{
  sessionId?: string | null;
  orgId: string;
  messageId: string;
  text: string;
  dontSendDeleteMessageReq?: boolean;
}>();

export const resetAIHelperAgentEvent = createEvent();

export const $aiHelperAgent = createStore<AIHelperAgentStore>(aiHelperAgentInitialState);

const $abortAgentController = createStore<AbortController | null>(null).on(
  abortAgentMessageEvent,
  (abortController) => {
    if (abortController !== null) {
      abortController.abort();
    }
    return null;
  }
);

$aiHelperAgent.on(resetAIHelperAgentEvent, () => {
  return { ...aiHelperAgentInitialState };
});

export const createAIHelperAgentThreadFx = createEffect<
  { orgId: string; agentId: string; workspaceId: string; input: AgentInput },
  { id: string; input: AgentThreadInput }
>(async ({ orgId, agentId, workspaceId, input }) => {
  const res = await axiosClient.post(
    `/ai-assistant/rest/orgs/${orgId}/agents/${agentId}/threads`,
    {
      input,
    },
    {
      params: {
        workspaceId,
      },
    }
  );

  return res.data;
});

const loadAIHelperAgentFx = createEffect<{ globalId: string; orgId: string }, AIAgent>(async ({ globalId, orgId }) => {
  const response = await aiServiceCommonApiClient.AgentApi.apiGetAgentByGlobalId(
    {
      org: orgId,
      id: globalId,
    },
    {
      headers: {
        'X-Need-Enrich': 'false',
      },
    }
  );

  return response.data as AIAgent;
});

$aiHelperAgent.on(loadAIHelperAgentEvent, () => ({
  state: 'loading',
}));

$aiHelperAgent.on(loadAIHelperAgentFx.doneData, (_, agent) => ({
  agent,
  state: 'loaded',
}));

$aiHelperAgent.on(loadAIHelperAgentFx.fail, () => ({
  state: 'error',
}));

sample({
  source: loadAIHelperAgentEvent,
  target: loadAIHelperAgentFx,
});

$aiThreads.on(createAgentMessageEvent, (state, { threadId, text, files, rewriteId }) => {
  const messages: ChatbotMessage[] = [];
  if (text || files) {
    messages.push({
      role: ChatbotMessageRole.USER,
      message: text || '',
      files: files?.map((file) => ({
        sfUUID: '',
        type: file.file.type,
        filename: file.file.name,
        tempFileUrl: URL.createObjectURL(file.file),
      })),
    });
  }

  let threadMessages: ChatbotMessage[] = [];
  if (threadId && state.threads && state.threads[threadId]) {
    threadMessages = (
      typeof state.threads[threadId].messages !== 'undefined' && state.threads[threadId]?.messages?.length
        ? state.threads[threadId].messages
        : []
    ) as ChatbotMessage[];

    if (rewriteId) {
      const rewriteIndex = threadMessages.findIndex((msg) => msg.id === rewriteId);
      if (rewriteIndex !== -1) {
        threadMessages = threadMessages.slice(0, rewriteIndex);
      }
    }
  }
  threadMessages.push(...messages);

  return Object.assign({}, state, {
    threads: {
      ...state.threads,
      [threadId]: Object.assign({}, state.threads[threadId], {
        messages: threadMessages,
        isWriting: true,
        updatedAt: new Date().getTime(),
        editMessageId: null,
      }),
    },
  });
});

/* On send message event create empty message in writingMessage store */
$writingMessage.on(createAgentMessageEvent, (_, payload) => {
  const message = {
    role: ChatbotMessageRole.AI,
    message: '',
    threadId: payload.threadId ?? null,
    isTextCompleted: false,
  };
  return message;
});

export const writeAgentMessageFx = createEffect(
  async ({
    sessionId,
    apiHost,
    orgId,
    workspaceId,
    threadId,
    text,
    abortController,
    onMessageChunkResolved,
    files,
    webPageDetails,
    rewriteId,
  }: SendAgentMessageParams) => {
    try {
      for await (const chunk of createAgentMessage({
        sessionId,
        orgId,
        apiHost,
        workspaceId,
        threadId,
        text,
        signal: abortController.signal,
        files: files?.map((file) => file.tempStoredFileName).filter((file): file is string => Boolean(file)) || [],
        webPageDetails,
        rewrite: rewriteId,
      })) {
        onMessageChunkResolved(chunk);
        if (chunk.type === MessageChunkType.DONE) {
          break;
        }
      }
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        onMessageChunkResolved({ type: MessageChunkType.DONE, content: 'aborted' } as MessageChunk);
      } else {
        throw error;
      }
    }
  }
);

sample({
  source: createAgentMessageEvent,
  target: writeAgentMessageFx,
});

/* Store the abort controller when creating an agent message */
sample({
  clock: createAgentMessageEvent,
  fn: ({ abortController }) => {
    return abortController;
  },
  target: $abortAgentController,
});

/* On stop writing message fx */
const stopAgentMessageFx = createEffect(
  async ({
    sessionId,
    orgId,
    messageId,
    text,
  }: {
    sessionId?: string | null;
    orgId: string;
    messageId: string;
    text: string;
  }) => {
    try {
      await axiosClient.post(
        `/ai-assistant/rest/orgs/${orgId}/agent-messages/${messageId}/delete`,
        { text },
        {
          headers: {
            'Content-Type': 'application/json',
            'everhelper-session-id': sessionId,
          },
        }
      );
    } catch (err) {
      return null;
    }
  }
);
sample({
  clock: abortAgentMessageEvent,
  filter: (data) => !data.dontSendDeleteMessageReq,
  target: stopAgentMessageFx,
});

export type AgentRunsHistory = {
  agentId: string;
  status: 'initial_loading' | 'loaded' | 'updating' | 'error';
  threads?: ChatbotThread[];
};
export const $agentsHistory = createStore<Record<string, AgentRunsHistory>>({});
export const fetchAgentThreadsEvent = createEvent<{ agentId: string; orgId: string }>();
export const deleteAgentThreadEvent = createEvent<{ agentId: string; threadId: string; orgId: string }>();

export const fetchAgentThreadsFx = createEffect(async (props: { orgId: string; agentId: string }) => {
  const res = await axiosClient.get(`/ai-assistant/rest/orgs/${props.orgId}/agents/${props.agentId}/threads`);
  if (res.status !== 200) {
    return { success: false };
  }
  return {
    success: true,
    threads: res.data as ChatbotThread[],
  };
});

sample({
  clock: fetchAgentThreadsEvent,
  source: [$agentsHistory],
  filter: (stores, params) => {
    const agentsHistory = stores[0] as Record<string, AgentRunsHistory>;
    if (!agentsHistory[params.agentId]) {
      return true;
    }
    return !['initial_loading', 'updating'].includes(agentsHistory[params.agentId].status);
  },
  fn: (_, params) => params,
  target: fetchAgentThreadsFx,
});

$agentsHistory.on(fetchAgentThreadsFx, (state, payload) => {
  const history = state[payload.agentId];
  return {
    ...state,
    [payload.agentId]: {
      ...history,
      status: !history ? 'initial_loading' : 'updating',
    },
  };
});

$agentsHistory.on(fetchAgentThreadsFx.done, (state, payload) => {
  const agentId = payload.params.agentId;
  if (!payload.result.success) {
    const showError = !state[agentId] || state[agentId].status === 'initial_loading';
    return {
      ...state,
      [agentId]: {
        ...(state[agentId] ?? {}),
        agentId,
        status: showError ? 'error' : 'loaded',
      },
    };
  }
  return {
    ...state,
    [agentId]: {
      ...(state[agentId] ?? {}),
      agentId,
      status: 'loaded',
      threads: payload.result.threads ?? [],
    },
  };
});

export const deleteAgentThreadFx = createEffect(
  async (params: { agentId: string; orgId: string; threadId: string }) => {
    const res = await axiosClient.delete(`/ai-assistant/rest/orgs/${params.orgId}/agent-threads/${params.threadId}`);
    if (res.status !== 200) {
      return { success: false };
    }
    return { success: true };
  }
);

sample({
  clock: deleteAgentThreadEvent,
  target: deleteAgentThreadFx,
});

$agentsHistory.on(deleteAgentThreadFx.done, (state, payload) => {
  if (!payload.result.success) {
    return state;
  }
  const agentId = payload.params.agentId;
  const threadId = payload.params.threadId;
  return {
    ...state,
    ...(!state[agentId]
      ? {}
      : {
          [agentId]: {
            ...state[agentId],
            threads: state[agentId].threads?.filter((thread) => thread.id !== threadId),
          },
        }),
  };
});

export enum AgentModeMainTabState {
  LOADING,
  START_VIEW,
  INPUT,
  CHAT,
  ERROR,
  AGENT_NOT_FOUND,
}
export const $chatbotAgentModeMainTabState = createStore<AgentModeMainTabState>(AgentModeMainTabState.LOADING);

export const setChatbotAgentModeMainTabStateEvent = createEvent<AgentModeMainTabState>();

$chatbotAgentModeMainTabState.on(setChatbotAgentModeMainTabStateEvent, (_, newState) => {
  return newState;
});
